import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { CheckCircle2, RefreshCw, Clock, X, CalendarPlus } from 'lucide-react';
import TrDateInput from '../components/TrDateInput';

interface IsPlanItem {
  isPlaniID: number;
  projeID: number;
  projeAdi: string;
  baslamaTarihi: string;
  bitisTarihi: string;
  sorumlu: string;
  durum: 'Bekliyor' | 'Devam Ediyor' | 'Tamamlandı';
  aciklama: string;
  aktifMi: boolean;
  eklemeTarihi: string;
}

interface Proje {
  projeID: number;
  projeAdi: string;
}

const IsPlan: React.FC = () => {
  const { user } = useAuth();
  const [planItems, setPlanItems] = useState<IsPlanItem[]>([]);
  const [projeler, setProjeler] = useState<Proje[]>([]);
  const [sorumlular, setSorumlular] = useState<string[]>([]);
  const [selectedProje, setSelectedProje] = useState<string>('');
  const [filterBaslangic, setFilterBaslangic] = useState<string>('');
  const [filterBitis, setFilterBitis] = useState<string>('');
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('timeline');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<IsPlanItem | null>(null);
  const [hoverDate, setHoverDate] = useState<{ date: Date; position: number } | null>(null);
  const [formData, setFormData] = useState<{
    projeID: number;
    baslamaTarihi: string;
    bitisTarihi: string;
    sorumlu: string;
    durum: 'Bekliyor' | 'Devam Ediyor' | 'Tamamlandı';
    aciklama: string;
  }>({
    projeID: 0,
    baslamaTarihi: '',
    bitisTarihi: '',
    sorumlu: '',
    durum: 'Bekliyor',
    aciklama: ''
  });

  useEffect(() => {
    fetchProjeler();
    fetchSorumlular();
    setDefaultDates();
    // İlk yüklemede plan çekmeyelim - proje seçilince çekilecek
  }, []);

  const setDefaultDates = () => {
    const today = new Date();
    const baslangic = new Date(today.getTime() - (15 * 24 * 60 * 60 * 1000)); // 15 gün öncesi
    const bitis = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 gün sonrası
    
    setFilterBaslangic(baslangic.toISOString().split('T')[0]);
    setFilterBitis(bitis.toISOString().split('T')[0]);
  };

  const fetchProjeler = async () => {
    try {
      const response = await axiosInstance.get('/api/Projeler');
      setProjeler(response.data);
    } catch (error) {
      console.error('Projeler yüklenirken hata:', error);
      toast.error('Projeler yüklenirken hata oluştu');
    }
  };

  const fetchSorumlular = async () => {
    try {
      const response = await axiosInstance.get('/api/IsPlanlari/sorumlular');
      setSorumlular(response.data);
    } catch (error) {
      console.error('Sorumlular yüklenirken hata:', error);
      // Sessiz hata - isteğe bağlı
    }
  };

  const fetchPlanItems = async () => {
    // Proje seçilmemişse plan getirme
    if (!selectedProje) {
      setPlanItems([]);
      return;
    }

    setLoading(true);
    try {
      let url = `/api/IsPlanlari?projeId=${selectedProje}`;
      
      // Tarih filtresi varsa ekle
      if (filterBaslangic) {
        url += `&baslangicTarihi=${filterBaslangic}`;
      }
      if (filterBitis) {
        url += `&bitisTarihi=${filterBitis}`;
      }
      
      const response = await axiosInstance.get(url);
      setPlanItems(response.data);
    } catch (error) {
      console.error('İş planı yüklenirken hata:', error);
      toast.error('İş planı yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (submitting) return; // Çift tıklama önleme
    setSubmitting(true);
    
    // Form validasyonu
    if (!formData.projeID || formData.projeID === 0) {
      toast.error('Proje seçimi zorunludur');
      setSubmitting(false);
      return;
    }
    
    if (!formData.baslamaTarihi || !formData.bitisTarihi) {
      toast.error('Başlama ve bitiş tarihleri zorunludur');
      setSubmitting(false);
      return;
    }
    
    if (new Date(formData.bitisTarihi) < new Date(formData.baslamaTarihi)) {
      toast.error('Bitiş tarihi başlama tarihinden önce olamaz');
      setSubmitting(false);
      return;
    }
    
    try {
      console.log('Gönderilen form data:', formData); // Debug için
      if (editingItem) {
        await axiosInstance.put(`/api/IsPlanlari/${editingItem.isPlaniID}`, formData);
        toast.success('İş planı güncellendi');
      } else {
        await axiosInstance.post('/api/IsPlanlari', formData);
        toast.success('İş planı eklendi');
      }
      
      fetchPlanItems();
      fetchSorumlular(); // Sorumlu listesini güncelle
      handleCloseModal();
    } catch (error) {
      console.error('İş planı kaydetme hatası:', error);
      toast.error('İş planı kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Bu öğeyi silmek istediğinizden emin misiniz?')) {
      try {
        await axiosInstance.delete(`/api/IsPlanlari/${id}`);
        toast.success('İş planı silindi');
        fetchPlanItems();
      } catch (error) {
        console.error('Silme hatası:', error);
        toast.error('Silme işlemi başarısız');
      }
    }
  };

  const handleEdit = (item: IsPlanItem) => {
    setEditingItem(item);
    setFormData({
      projeID: item.projeID,
      baslamaTarihi: item.baslamaTarihi.split('T')[0],
      bitisTarihi: item.bitisTarihi.split('T')[0],
      sorumlu: item.sorumlu,
      durum: item.durum,
      aciklama: item.aciklama
    });
    setShowModal(true);
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleAddNew = () => {
    setEditingItem(null);
    const todayDate = getTodayDate();
    setFormData({
      projeID: selectedProje ? parseInt(selectedProje) : 0,
      baslamaTarihi: todayDate,
      bitisTarihi: todayDate,
      sorumlu: '',
      durum: 'Bekliyor',
      aciklama: ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    const todayDate = getTodayDate();
    setFormData({
      projeID: selectedProje ? parseInt(selectedProje) : 0,
      baslamaTarihi: todayDate,
      bitisTarihi: todayDate,
      sorumlu: '',
      durum: 'Bekliyor',
      aciklama: ''
    });
  };

  const getDurumCfg = (durum: string) => {
    switch (durum) {
      case 'Tamamlandı': return { bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0', shadow: 'rgba(16,185,129,0.15)', pillBg: '#ecfdf5', icon: <CheckCircle2 size={11} /> };
      case 'Devam Ediyor': return { bg: '#fffbeb', color: '#92400e', border: '#fde68a', shadow: 'rgba(245,158,11,0.15)', pillBg: '#fffbeb', icon: <RefreshCw size={11} /> };
      default: return { bg: '#f8fafc', color: '#475569', border: '#e2e8f0', shadow: 'rgba(100,116,139,0.12)', pillBg: '#f8fafc', icon: <Clock size={11} /> };
    }
  };

  const getDurumBadge = (durum: string) => {
    const cfg = getDurumCfg(durum);
    return <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11.5, fontWeight:700, padding:'4px 11px', borderRadius:20, background:cfg.pillBg, color:cfg.color, border:`1px solid ${cfg.border}` }}>{cfg.icon} {durum}</span>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  // Timeline için sorumlu grupları oluştur
  const getGroupedBySorumlu = () => {
    const groups: { [key: string]: IsPlanItem[] } = {};
    
    planItems.forEach(item => {
      if (!groups[item.sorumlu]) {
        groups[item.sorumlu] = [];
      }
      groups[item.sorumlu].push(item);
    });

    // Her grubu tarih sırasına göre sırala
    Object.keys(groups).forEach(sorumlu => {
      groups[sorumlu].sort((a, b) => new Date(a.baslamaTarihi).getTime() - new Date(b.baslamaTarihi).getTime());
    });

    return groups;
  };

  // Tarih aralığı hesapla
  const getDateRange = () => {
    if (planItems.length === 0) return { start: new Date(), end: new Date() };
    
    const dates = planItems.flatMap(item => [
      new Date(item.baslamaTarihi),
      new Date(item.bitisTarihi)
    ]);
    
    return {
      start: new Date(Math.min(...dates.map(d => d.getTime()))),
      end: new Date(Math.max(...dates.map(d => d.getTime())))
    };
  };

  // Görevin timeline pozisyonunu hesapla
  const getTaskPosition = (item: IsPlanItem, dateRange: { start: Date; end: Date }) => {
    const totalDays = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const startOffset = Math.ceil((new Date(item.baslamaTarihi).getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const taskDays = Math.ceil((new Date(item.bitisTarihi).getTime() - new Date(item.baslamaTarihi).getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    return {
      left: (startOffset / totalDays) * 100,
      width: (taskDays / totalDays) * 100
    };
  };

  // getDurumColor → use getDurumCfg(durum).bg instead
  const getDurumColor = (durum: string) => getDurumCfg(durum).bg;

  // Hover tarihinde olan görevleri kontrol et
  const isTaskInHoverDate = (task: IsPlanItem, hoverDate: Date) => {
    if (!hoverDate) return false;
    
    const taskStart = new Date(task.baslamaTarihi);
    const taskEnd = new Date(task.bitisTarihi);
    
    // Hover tarihini gün bazında karşılaştır
    const hoverDateOnly = new Date(hoverDate.getFullYear(), hoverDate.getMonth(), hoverDate.getDate());
    const taskStartOnly = new Date(taskStart.getFullYear(), taskStart.getMonth(), taskStart.getDate());
    const taskEndOnly = new Date(taskEnd.getFullYear(), taskEnd.getMonth(), taskEnd.getDate());
    
    return hoverDateOnly >= taskStartOnly && hoverDateOnly <= taskEndOnly;
  };

  // Timeline mouse move handler
  const handleTimelineMouseMove = (e: React.MouseEvent, dateRange: { start: Date; end: Date }) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const percentage = relativeX / rect.width;
    
    const totalDays = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const dayOffset = Math.floor(percentage * totalDays);
    
    const hoverDate = new Date(dateRange.start);
    hoverDate.setDate(hoverDate.getDate() + dayOffset);
    
    setHoverDate({
      date: hoverDate,
      position: percentage * 100
    });
  };

  // Timeline mouse leave handler
  const handleTimelineMouseLeave = () => {
    setHoverDate(null);
  };

  return (
    <>
      <style>{`
        @keyframes ip-spin { to { transform: rotate(360deg); } }
        .ip-wrap { background: #f8fafc; min-height: 100vh; padding: 24px 28px; font-family: 'Inter','Segoe UI',sans-serif; }
        /* Topbar */
        .ip-topbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
        .ip-page-title { font-size: 20px; font-weight: 800; color: #0f172a; display: flex; align-items: center; gap: 10px; letter-spacing: -0.4px; }
        /* Controls */
        .ip-controls { display: flex; align-items: center; gap: 12px; }
        .ip-view-toggle { display: flex; background: #f3f4f6; border-radius: 10px; padding: 4px; gap: 4px; }
        .ip-toggle-btn { border: none; background: none; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; color: #6b7280; cursor: pointer; font-family: inherit; transition: all 0.2s; }
        .ip-toggle-active { background: #cd6906; color: #fff; box-shadow: 0 2px 8px rgba(205,105,6,0.25); }
        .ip-btn-add { background: #cd6906; color: #fff; border: none; border-radius: 8px; padding: 10px 20px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; box-shadow: 0 4px 12px rgba(205,105,6,0.3); transition: all 0.2s; }
        .ip-btn-add:hover:not(:disabled) { background: #b85d05; box-shadow: 0 4px 12px rgba(205,105,6,0.3); transform: translateY(-1px); }
        .ip-btn-add:disabled { opacity: 0.45; cursor: not-allowed; }
        /* Filters */
        .ip-filters { display: flex; align-items: flex-end; gap: 14px; background: #fff; border-radius: 14px; padding: 16px 20px; margin-bottom: 18px; box-shadow: 0 1px 6px rgba(0,0,0,0.05); border: 1px solid #f1f5f9; flex-wrap: wrap; }
        .ip-filter-group { display: flex; flex-direction: column; gap: 5px; min-width: 180px; }
        .ip-filter-label { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: none; letter-spacing: 0; }
        .ip-filter-input, .ip-filter-select { height: 38px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 0 12px; font-size: 13px; color: #0f172a; font-family: inherit; outline: none; transition: border-color 0.2s; background: #fff; }
        .ip-filter-select { min-width: 200px; }
        .ip-filter-input:focus, .ip-filter-select:focus { border-color: #cd6906; }
        .ip-btn-search { height: 38px; background: #cd6906; color: #fff; border: none; border-radius: 8px; padding: 0 20px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; white-space: nowrap; transition: background 0.2s; box-shadow: 0 4px 12px rgba(205,105,6,0.3); }
        .ip-btn-search:hover:not(:disabled) { background: #b85d05; }
        .ip-btn-search:disabled { opacity: 0.4; cursor: not-allowed; }
        /* Card */
        .ip-card { background: #fff; border-radius: 16px; box-shadow: 0 2px 12px rgba(0,0,0,0.05); border: 1px solid #f1f5f9; overflow: hidden; }
        /* States */
        .ip-center-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; gap: 14px; color: #94a3b8; font-size: 13px; }
        .ip-spinner { width: 30px; height: 30px; border: 3px solid #e2e8f0; border-top-color: #cd6906; border-radius: 50%; animation: ip-spin 0.8s linear infinite; }
        .ip-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; }
        .ip-empty-icon { font-size: 40px; margin-bottom: 12px; }
        .ip-empty-text { font-size: 14px; color: #94a3b8; text-align: center; }
        /* Timeline */
        .ip-timeline { padding: 20px 24px; overflow-x: auto; }
        .ip-tl-header { display: flex; align-items: stretch; margin-bottom: 8px; }
        .ip-tl-sorumlu-col { width: 210px; min-width: 210px; flex-shrink: 0; padding-right: 16px; display: flex; align-items: flex-end; padding-bottom: 6px; }
        .ip-tl-track-col { flex: 1; min-width: 0; }
        .ip-tl-col-label { font-size: 10.5px; font-weight: 700; color: #94a3b8; text-transform: none; letter-spacing: 0; }
        .ip-tl-meta { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .ip-tl-date-label { font-size: 11px; font-weight: 600; color: #64748b; }
        .ip-tl-duration { font-size: 11px; font-weight: 700; color: #cd6906; background: #fff8f0; padding: 2px 8px; border-radius: 10px; }
        .ip-tl-scale { position: relative; height: 28px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; overflow: visible; }
        .ip-tl-tick { position: absolute; top: 0; height: 100%; display: flex; flex-direction: column; align-items: center; }
        .ip-tick-line { width: 1px; height: 100%; background: #cbd5e1; }
        .ip-tick-label { font-size: 9.5px; font-weight: 600; color: #94a3b8; white-space: nowrap; transform: translateX(-50%); margin-top: 2px; position: absolute; top: 100%; }
        /* TL rows */
        .ip-tl-row { display: flex; align-items: flex-start; padding: 10px 0; border-top: 1px solid #f1f5f9; }
        .ip-tl-row:last-child { border-bottom: 1px solid #f1f5f9; }
        .ip-sorumlu-card { display: flex; align-items: center; gap: 9px; padding-right: 16px; width: 210px; min-width: 210px; flex-shrink: 0; }
        .ip-sorumlu-avatar { width: 36px; height: 36px; border-radius: 50%; background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; letter-spacing: 0.5px; }
        .ip-sorumlu-name { font-size: 12px; font-weight: 700; color: #0f172a; line-height: 1.3; word-break: break-word; }
        /* Track */
        .ip-tl-track-wrap { flex: 1; min-width: 0; }
        .ip-tl-track { position: relative; background: #f8fafc; border: 1px solid #e8eef6; border-radius: 10px; width: 100%; overflow: visible; cursor: crosshair; }
        .ip-tl-grid-line { position: absolute; top: 0; bottom: 0; width: 1px; background: rgba(148,163,184,0.2); pointer-events: none; }
        .ip-today-line { position: absolute; top: -10px; bottom: -10px; width: 2px; background: #ef4444; z-index: 5; pointer-events: none; }
        .ip-today-label { position: absolute; top: -22px; left: 50%; transform: translateX(-50%); background: #ef4444; color: #fff; font-size: 9px; font-weight: 800; padding: 2px 6px; border-radius: 4px; white-space: nowrap; letter-spacing: 0.5px; }
        .ip-hover-line { position: absolute; top: -10px; bottom: -10px; width: 1.5px; background: #cd6906; z-index: 10; pointer-events: none; }
        .ip-hover-label { position: absolute; top: -22px; left: 50%; transform: translateX(-50%); background: #cd6906; color: #fff; font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 4px; white-space: nowrap; box-shadow: 0 2px 8px rgba(205,105,6,0.3); }
        .ip-task-bar { position: absolute; display: flex; align-items: center; gap: 5px; padding: 0 9px; height: 26px; border-radius: 7px; cursor: pointer; font-size: 11px; font-weight: 500; transition: all 0.2s ease; overflow: hidden; white-space: nowrap; }
        .ip-task-bar:hover { transform: translateY(-2px) !important; z-index: 25 !important; opacity: 1 !important; }
        .ip-task-icon { font-size: 11px; flex-shrink: 0; }
        .ip-task-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        /* Table */
        .ip-table-wrap { overflow-x: auto; }
        .ip-table { width: 100%; border-collapse: collapse; font-size: 13.5px; font-family: 'Inter','Segoe UI',sans-serif; }
        .ip-table thead tr { border-bottom: 2px solid #f1f5f9; }
        .ip-table th { padding: 14px 16px; font-size: 10.5px; font-weight: 700; color: #94a3b8; text-transform: none; letter-spacing: 0; text-align: left; }
        .ip-table td { padding: 14px 16px; color: #0f172a; border-bottom: 1px solid #f8fafc; vertical-align: middle; }
        .ip-table tbody tr { transition: background 0.15s; }
        .ip-table tbody tr:hover { background: #f8fafc; }
        .ip-table tbody tr:last-child td { border-bottom: none; }
        .ip-aciklama-cell { color: #64748b; max-width: 220px; }
        .ip-actions-cell { text-align: right; white-space: nowrap; }
        .ip-icon-btn { border: none; background: none; cursor: pointer; padding: 5px 7px; border-radius: 8px; font-size: 15px; transition: background 0.15s; opacity: 0.45; }
        .ip-icon-btn:hover { opacity: 1; }
        .ip-edit-btn:hover { background: #eff6ff; }
        .ip-del-btn:hover { background: #fef2f2; }
        .ip-durum-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 700; padding: 4px 11px; border-radius: 20px; }
        /* Modal */
        .ip-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.4); backdrop-filter: blur(4px); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 24px; }
        .ip-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(15,23,42,0.2); }
        .ip-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #f1f5f9; }
        .ip-modal-title { font-size: 16px; font-weight: 800; color: #0f172a; }
        .ip-modal-close { border: none; background: #f1f5f9; width: 28px; height: 28px; border-radius: 50%; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; color: #64748b; transition: all 0.2s; }
        .ip-modal-close:hover { background: #e2e8f0; color: #0f172a; }
        .ip-modal-body { padding: 20px 24px; }
        .ip-modal-footer { padding: 14px 24px 20px; display: flex; justify-content: flex-end; gap: 10px; border-top: 1px solid #f1f5f9; }
        .ip-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .ip-form-field { display: flex; flex-direction: column; gap: 5px; }
        .ip-span2 { grid-column: 1 / -1; }
        .ip-form-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: none; letter-spacing: 0; }
        .ip-form-input, .ip-form-select { height: 40px; border: 1.5px solid #e2e8f0; border-radius: 9px; padding: 0 12px; font-size: 13px; color: #0f172a; font-family: inherit; outline: none; transition: border-color 0.2s; background: #fff; }
        .ip-form-input:focus, .ip-form-select:focus { border-color: #cd6906; }
        .ip-form-textarea { border: 1.5px solid #e2e8f0; border-radius: 9px; padding: 10px 12px; font-size: 13px; color: #0f172a; font-family: inherit; outline: none; resize: vertical; transition: border-color 0.2s; width: 100%; box-sizing: border-box; }
        .ip-form-textarea:focus { border-color: #cd6906; }
        .ip-btn-cancel { border: 1.5px solid #e2e8f0; background: #fff; color: #64748b; border-radius: 9px; padding: 9px 20px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; }
        .ip-btn-save { background: #cd6906; color: #fff; border: none; border-radius: 8px; padding: 10px 20px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; display: flex; align-items: center; gap: 6px; box-shadow: 0 4px 12px rgba(205,105,6,0.3); transition: background 0.15s; }
        .ip-btn-save:hover:not(:disabled) { background: #b85d05; }
        .ip-btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
        .ip-spinner-sm { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: ip-spin 0.7s linear infinite; display: inline-block; }
      `}</style>

      <div className="ip-wrap">
        {/* Topbar */}
        <div className="ip-topbar">
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.02em' }}>İş Planı</h1>
              <p style={{ fontSize: '13.5px', color: '#6b7280', margin: 0 }}>Aylık görevlerinizi, toplantılarınızı ve planlamalarınızı yönetin.</p>
            </div>
          </div>
          <div className="ip-controls">
            <div className="ip-view-toggle">
              <button className={`ip-toggle-btn${viewMode === 'timeline' ? ' ip-toggle-active' : ''}`} onClick={() => setViewMode('timeline')}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="14" height="14" style={{display:'inline',verticalAlign:'middle',marginRight:4}}><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
                Timeline
              </button>
              <button className={`ip-toggle-btn${viewMode === 'table' ? ' ip-toggle-active' : ''}`} onClick={() => setViewMode('table')}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="14" height="14" style={{display:'inline',verticalAlign:'middle',marginRight:4}}><path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0c0 .621.504 1.125 1.125 1.125M20.25 8.25v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h17.25" /></svg>
                Tablo
              </button>
            </div>
            <button className="ip-btn-add" onClick={handleAddNew} disabled={!selectedProje}>+ Yeni Plan Ekle</button>
          </div>
        </div>

        {/* Filters */}
        <div className="ip-filters">
          <div className="ip-filter-group">
            <label className="ip-filter-label">Proje</label>
            <select className="ip-filter-select" value={selectedProje} onChange={(e) => setSelectedProje(e.target.value)}>
              <option value="">Proje Seçin...</option>
              {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
            </select>
          </div>
          <div className="ip-filter-group">
            <label className="ip-filter-label">Başlangıç</label>
            <TrDateInput className="ip-filter-input" value={filterBaslangic} onChange={(e) => setFilterBaslangic(e.target.value)} />
          </div>
          <div className="ip-filter-group">
            <label className="ip-filter-label">Bitiş</label>
            <TrDateInput className="ip-filter-input" value={filterBitis} onChange={(e) => setFilterBitis(e.target.value)} />
          </div>
          <div className="ip-filter-group" style={{ alignSelf: 'flex-end' }}>
            <button className="ip-btn-search" onClick={() => fetchPlanItems()} disabled={!selectedProje}>Göster</button>
          </div>
        </div>

        {/* Main Card */}
        <div className="ip-card">
          {loading ? (
            <div className="ip-center-state">
              <div className="ip-spinner"></div>
              <span>Yükleniyor...</span>
            </div>
          ) : planItems.length === 0 ? (
            <div className="ip-empty">
              <div style={{ color: '#d1d5db', marginBottom: 16 }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1" stroke="#d1d5db" width="64" height="64"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" /></svg>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                İş planını görüntülemek için bir proje seçin
              </h3>
              <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 20, textAlign: 'center' }}>
                {selectedProje ? 'Bu kriterlere uygun iş planı bulunamadı.' : 'Yukarıdaki açılır menüden proje seçtikten sonra tarih aralığı belirleyin.'}
              </p>
            </div>
          ) : viewMode === 'timeline' ? (
            /* ── TIMELINE VIEW ── */
            <div className="ip-timeline">
              {(() => {
                const groupedData = getGroupedBySorumlu();
                const dateRange = getDateRange();
                const totalDays = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                const maxTicks = Math.min(totalDays, 12);
                const tickInterval = Math.ceil(totalDays / maxTicks);
                return (
                  <>
                    {/* Header row */}
                    <div className="ip-tl-header">
                      <div className="ip-tl-sorumlu-col">
                        <span className="ip-tl-col-label">Sorumlular</span>
                      </div>
                      <div className="ip-tl-track-col">
                        <div className="ip-tl-meta">
                          <span className="ip-tl-date-label">{formatDate(dateRange.start.toISOString())}</span>
                          <span className="ip-tl-duration">{totalDays} gün</span>
                          <span className="ip-tl-date-label">{formatDate(dateRange.end.toISOString())}</span>
                        </div>
                        <div className="ip-tl-scale">
                          {Array.from({ length: maxTicks }, (_, i) => {
                            const dayOffset = i * tickInterval;
                            const date = new Date(dateRange.start);
                            date.setDate(date.getDate() + dayOffset);
                            const pos = Math.min((dayOffset / totalDays) * 100, 97);
                            return (
                              <div key={i} className="ip-tl-tick" style={{ left: `${pos}%` }}>
                                <div className="ip-tick-line"></div>
                                <span className="ip-tick-label">{date.getDate()}/{date.getMonth() + 1}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Sorumlu rows */}
                    {Object.entries(groupedData).map(([sorumlu, tasks]) => (
                      <div key={sorumlu} className="ip-tl-row">
                        <div className="ip-sorumlu-card">
                          <div className="ip-sorumlu-avatar">
                            {sorumlu.split(' ').map((w: string) => w[0]).slice(0, 2).join('')}
                          </div>
                          <div className="ip-sorumlu-name">{sorumlu}</div>
                        </div>
                        <div className="ip-tl-track-wrap">
                          <div
                            className="ip-tl-track"
                            style={{ height: `${Math.max(44, tasks.length * 32 + 12)}px` }}
                            onMouseMove={(e) => handleTimelineMouseMove(e, dateRange)}
                            onMouseLeave={handleTimelineMouseLeave}
                          >
                            {/* Grid lines */}
                            {Array.from({ length: maxTicks }, (_, i) => {
                              const pos = Math.min(((i * tickInterval) / totalDays) * 100, 97);
                              return <div key={`g-${i}`} className="ip-tl-grid-line" style={{ left: `${pos}%` }} />;
                            })}

                            {/* Today line */}
                            {(() => {
                              const today = new Date();
                              if (today >= dateRange.start && today <= dateRange.end) {
                                const daysSince = Math.ceil((today.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
                                const pos = (daysSince / totalDays) * 100;
                                return (
                                  <div className="ip-today-line" style={{ left: `${pos}%` }}>
                                    <div className="ip-today-label">BUGÜN</div>
                                  </div>
                                );
                              }
                              return null;
                            })()}

                            {/* Hover line */}
                            {hoverDate && (
                              <div className="ip-hover-line" style={{ left: `${Math.min(hoverDate.position, 96)}%` }}>
                                <div className="ip-hover-label">
                                  {hoverDate.date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </div>
                              </div>
                            )}

                            {/* Task bars */}
                            {tasks.map((task, index) => {
                              const pos = getTaskPosition(task, dateRange);
                              const isHighlighted = hoverDate ? isTaskInHoverDate(task, hoverDate.date) : false;
                              const cfg = getDurumCfg(task.durum);
                              return (
                                <div
                                  key={task.isPlaniID}
                                  className="ip-task-bar"
                                  style={{
                                    left: `${pos.left}%`,
                                    width: `${Math.max(pos.width, 4)}%`,
                                    top: `${index * 32 + 6}px`,
                                    background: cfg.bg,
                                    color: cfg.color,
                                    border: `1.5px solid ${cfg.border}`,
                                    opacity: hoverDate ? (isHighlighted ? 1 : 0.3) : 1,
                                    boxShadow: isHighlighted ? `0 4px 16px ${cfg.shadow}` : `0 1px 4px ${cfg.shadow}`,
                                    transform: isHighlighted ? 'translateY(-2px)' : 'translateY(0)',
                                    zIndex: isHighlighted ? 20 : 10,
                                  }}
                                  title={`${task.aciklama} (${formatDate(task.baslamaTarihi)} – ${formatDate(task.bitisTarihi)})`}
                                  onClick={() => handleEdit(task)}
                                >
                                  <span className="ip-task-icon">{cfg.icon}</span>
                                  <span className="ip-task-label">{task.aciklama}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          ) : (
            /* ── TABLE VIEW ── */
            <div className="ip-table-wrap">
              <table className="ip-table">
                <thead>
                  <tr>
                    <th>Proje</th>
                    <th>Sorumlu</th>
                    <th>Başlama</th>
                    <th>Bitiş</th>
                    <th>Durum</th>
                    <th>Açıklama</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {planItems.map(item => {
                    const cfg = getDurumCfg(item.durum);
                    return (
                      <tr key={item.isPlaniID}>
                        <td><strong>{item.projeAdi}</strong></td>
                        <td>{item.sorumlu}</td>
                        <td>{formatDate(item.baslamaTarihi)}</td>
                        <td>{formatDate(item.bitisTarihi)}</td>
                        <td>
                          <span className="ip-durum-badge" style={{ background: cfg.pillBg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                            {cfg.icon} {item.durum}
                          </span>
                        </td>
                        <td className="ip-aciklama-cell">{item.aciklama}</td>
                        <td className="ip-actions-cell">
                          <button className="ip-icon-btn ip-edit-btn" onClick={() => handleEdit(item)} title="Düzenlegi">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="14" height="14"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" /></svg>
                          </button>
                          <button className="ip-icon-btn ip-del-btn" onClick={() => handleDelete(item.isPlaniID)} title="Sil">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="14" height="14"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (() => {
        const inp: React.CSSProperties = {
          width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
          borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff',
          color: '#111827', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s',
        };
        const lbl: React.CSSProperties = {
          display: 'block', marginBottom: 5, fontWeight: 500, color: '#374151', fontSize: 12,
        };
        const onFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
          e.currentTarget.style.borderColor = '#111827';
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)';
        };
        const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
          e.currentTarget.style.borderColor = '#d1d5db';
          e.currentTarget.style.boxShadow = 'none';
        };
        return (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 1050,
              background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            onClick={handleCloseModal}
          >
            <div
              style={{
                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 576,
                maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                display: 'flex', flexDirection: 'column',
                fontFamily: "'Inter','Segoe UI',sans-serif",
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: 18 }}>
                      {editingItem ? 'İş Planı Düzenle' : 'Yeni İş Planı'}
                    </h4>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                      Takviminize yeni bir görev veya etkinlik planlayın.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'color 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#374151')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <form onSubmit={handleSubmit} style={{ flex: 1 }}>
                <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

                  {/* Proje */}
                  <div>
                    <label style={lbl}>Proje *</label>
                    <select
                      value={formData.projeID}
                      onChange={e => setFormData({ ...formData, projeID: parseInt(e.target.value) })}
                      required
                      style={inp}
                      onFocus={onFocus}
                      onBlur={onBlur}
                    >
                      <option value={0}>Proje Seçin</option>
                      {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
                    </select>
                  </div>

                  {/* Tarihler */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <label style={lbl}>Başlama Tarihi *</label>
                      <TrDateInput
                        value={formData.baslamaTarihi}
                        onChange={e => setFormData({ ...formData, baslamaTarihi: e.target.value, bitisTarihi: e.target.value })}
                        required
                        style={{ ...inp }}
                        onFocus={onFocus}
                        onBlur={onBlur}
                      />
                    </div>
                    <div>
                      <label style={lbl}>Bitiş Tarihi *</label>
                      <TrDateInput
                        value={formData.bitisTarihi}
                        onChange={e => setFormData({ ...formData, bitisTarihi: e.target.value })}
                        required
                        style={{ ...inp }}
                        onFocus={onFocus}
                        onBlur={onBlur}
                      />
                    </div>
                  </div>

                  {/* Sorumlu + Durum */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <label style={lbl}>Sorumlu *</label>
                      <input
                        type="text"
                        value={formData.sorumlu}
                        onChange={e => setFormData({ ...formData, sorumlu: e.target.value })}
                        placeholder="Sorumlu kişi adı"
                        list="sorumlular-datalist"
                        required
                        style={inp}
                        onFocus={onFocus}
                        onBlur={onBlur}
                      />
                      <datalist id="sorumlular-datalist">
                        {sorumlular.map((s, i) => <option key={i} value={s} />)}
                      </datalist>
                    </div>
                    <div>
                      <label style={lbl}>Durum</label>
                      <select
                        value={formData.durum}
                        onChange={e => setFormData({ ...formData, durum: e.target.value as any })}
                        style={inp}
                        onFocus={onFocus}
                        onBlur={onBlur}
                      >
                        <option value="Bekliyor">Bekliyor</option>
                        <option value="Devam Ediyor">Devam Ediyor</option>
                        <option value="Tamamlandı">Tamamlandı</option>
                      </select>
                    </div>
                  </div>

                  {/* Açıklama */}
                  <div>
                    <label style={lbl}>Açıklama</label>
                    <textarea
                      value={formData.aciklama}
                      onChange={e => setFormData({ ...formData, aciklama: e.target.value })}
                      placeholder="İş planının detaylarını yazın..."
                      rows={4}
                      style={{ ...inp, minHeight: 100, resize: 'vertical' } as React.CSSProperties}
                      onFocus={onFocus as any}
                      onBlur={onBlur as any}
                    />
                  </div>
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 28px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      padding: '9px 22px', background: submitting ? '#e5e7eb' : '#cd6906',
                      color: submitting ? '#9ca3af' : '#fff',
                      border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 7,
                      boxShadow: submitting ? 'none' : '0 1px 3px rgba(205,105,6,0.3)',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!submitting) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; } }}
                    onMouseLeave={e => { if (!submitting) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; } }}
                  >
                    {submitting
                      ? 'Kaydediliyor...'
                      : <><CalendarPlus size={14} /> {editingItem ? 'Güncelle' : 'Kaydet'}</>
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default IsPlan;